import { useMemo, useState } from 'react';
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import { WorshipReport } from '../../types/worshipReport';
import { Users } from 'lucide-react';

interface Props {
  reports: WorshipReport[];
}

const PERIODS = [
  { id: '1m', label: '1 mois', months: 1 },
  { id: '3m', label: '3 mois', months: 3 },
  { id: '6m', label: '6 mois', months: 6 },
  { id: '1a', label: '1 an', months: 12 },
  { id: 'all', label: 'Tout', months: 0 },
];

const VIEWS = [
  { id: 'presence', label: 'Présence' },
  { id: 'conversions', label: 'Conversions' },
  { id: 'repartition', label: 'H / F' },
];

export default function WorshipTrendChart({ reports }: Props) {
  const [selectedPeriod, setSelectedPeriod] = useState('3m');
  const [selectedView, setSelectedView] = useState('presence');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  const usingCustomRange = !!(startDate || endDate);

  const filtered = useMemo(() => {
    let data = [...reports];
    if (usingCustomRange) {
      if (startDate) data = data.filter(r => r.date >= startDate);
      if (endDate) data = data.filter(r => r.date <= endDate);
    } else if (selectedPeriod !== 'all') {
      const p = PERIODS.find(p => p.id === selectedPeriod);
      if (p && p.months > 0) {
        const cut = new Date();
        cut.setMonth(cut.getMonth() - p.months);
        const cutStr = cut.toISOString().split('T')[0];
        data = data.filter(r => r.date >= cutStr);
      }
    }
    return data.sort((a, b) => a.date.localeCompare(b.date));
  }, [reports, selectedPeriod, startDate, endDate, usingCustomRange]);

  const chartData = useMemo(() => filtered.map(r => {
    const att = r.attendance || {} as any;
    const adults = (att.adults?.men || 0) + (att.adults?.women || 0);
    const children = (att.children?.boys || 0) + (att.children?.girls || 0);
    const convTotal = (att.conversions?.men || 0) + (att.conversions?.women || 0);
    const nm = r.newMembers || {} as any;
    const newMbr = (nm.men || 0) + (nm.women || 0) + (nm.blooms || 0) + (nm.children || 0);
    return {
      date: new Date(r.date).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' }),
      adultes: adults,
      enfants: children,
      total: r.totalParticipants || (adults + children),
      hommes: (att.adults?.men || 0) + (att.children?.boys || 0),
      femmes: (att.adults?.women || 0) + (att.children?.girls || 0),
      conversions: convTotal,
      nouveaux: newMbr,
    };
  }), [filtered]);

  const totals = useMemo(() => ({
    participants: filtered.reduce((s, r) => s + (r.totalParticipants || 0), 0),
    conversions: filtered.reduce((s, r) => s + ((r.attendance?.conversions?.men || 0) + (r.attendance?.conversions?.women || 0)), 0),
    nouveaux: filtered.reduce((s, r) => s + (r.totalNewMembers || 0), 0),
  }), [filtered]);

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
      <div className="flex items-center gap-2 mb-4">
        <Users className="h-5 w-5 text-[#00665C]" />
        <h2 className="text-lg font-semibold text-gray-900">Évolution du culte</h2>
      </div>

      {/* Résumé */}
      <div className="grid grid-cols-3 gap-3 mb-5">
        <div className="bg-gray-50 rounded-lg p-3 text-center">
          <div className="text-xl font-bold text-[#00665C]">{totals.participants}</div>
          <div className="text-xs text-gray-500">Total participants</div>
        </div>
        <div className="bg-gray-50 rounded-lg p-3 text-center">
          <div className="text-xl font-bold text-blue-600">{totals.conversions}</div>
          <div className="text-xs text-gray-500">Total conversions</div>
        </div>
        <div className="bg-gray-50 rounded-lg p-3 text-center">
          <div className="text-xl font-bold text-purple-600">{totals.nouveaux}</div>
          <div className="text-xs text-gray-500">Nouveaux membres</div>
        </div>
      </div>

      {/* Onglets vue */}
      <div className="flex gap-2 mb-3">
        {VIEWS.map(v => (
          <button key={v.id} onClick={() => setSelectedView(v.id)}
            className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-colors ${selectedView === v.id ? 'bg-[#00665C] text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
          >{v.label}</button>
        ))}
      </div>

      {/* Filtres période + dates */}
      <div className="flex flex-wrap gap-2 items-end mb-4">
        <div className="flex gap-2 flex-wrap">
          {PERIODS.map(p => (
            <button key={p.id}
              onClick={() => { setSelectedPeriod(p.id); setStartDate(''); setEndDate(''); }}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap transition-colors ${
                !usingCustomRange && selectedPeriod === p.id
                  ? 'bg-[#00665C] text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >{p.label}</button>
          ))}
        </div>
        <div className="flex gap-2 items-end ml-auto">
          <div>
            <label className="block text-xs text-gray-500 mb-1">Du</label>
            <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)}
              className="px-2 py-1.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#00665C] focus:border-[#00665C]"
            />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Au</label>
            <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)}
              className="px-2 py-1.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#00665C] focus:border-[#00665C]"
            />
          </div>
          {usingCustomRange && (
            <button onClick={() => { setStartDate(''); setEndDate(''); }}
              className="px-3 py-1.5 text-xs text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg"
            >Réinitialiser</button>
          )}
        </div>
      </div>

      {/* Graphique */}
      {chartData.length === 0 ? (
        <div className="h-[280px] flex items-center justify-center text-gray-400">
          <div className="text-center">
            <Users className="h-12 w-12 mx-auto mb-3 opacity-30" />
            <p>Aucune donnée pour cette période</p>
          </div>
        </div>
      ) : selectedView === 'presence' ? (
        <div className="h-[280px] md:h-[320px] w-full">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={chartData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="date" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
              <Tooltip />
              <Legend />
              <Line type="monotone" dataKey="total" name="Total" stroke="#00665C" strokeWidth={2} dot={{ r: 4 }} activeDot={{ r: 6 }} />
              <Line type="monotone" dataKey="adultes" name="Adultes" stroke="#3B82F6" strokeWidth={2} dot={{ r: 3 }} />
              <Line type="monotone" dataKey="enfants" name="Enfants" stroke="#F59E0B" strokeWidth={2} dot={{ r: 3 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      ) : selectedView === 'conversions' ? (
        <div className="h-[280px] md:h-[320px] w-full">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="date" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
              <Tooltip />
              <Legend />
              <Bar dataKey="conversions" name="Conversions" fill="#3B82F6" radius={[4,4,0,0]} />
              <Bar dataKey="nouveaux" name="Nouveaux membres" fill="#A855F7" radius={[4,4,0,0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      ) : (
        <div className="h-[280px] md:h-[320px] w-full">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="date" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
              <Tooltip />
              <Legend />
              <Bar dataKey="hommes" name="Hommes" fill="#3B82F6" radius={[4,4,0,0]} stackId="a" />
              <Bar dataKey="femmes" name="Femmes" fill="#EC4899" radius={[4,4,0,0]} stackId="a" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}